// File: src/components/TopNav.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dropdown from "./Dropdown";
// import ProfileMenu from "./ProfileMenu";
import { Icon } from "../icons";
import { useTheme } from "../context/ThemeContext";
import swal from "sweetalert";

import { ViewCompanyDetails } from "../import/ApiEndPoint/ApiCalling";
import { baseURL } from "../import/ApiEndPoint/TestApi";
import SupportSessionDialog from "./SupportSessionDialog";

// ✅ CHANGE THIS IF YOUR BACKEND BASE URL IS DIFFERENT
const VERIFY_SUPPORT_CODE_URL = `${baseURL}/api/support-code/verify`;

export default function TopNav({ menus = [], onOpenPalette }) {
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedFY, setSelectedFY] = useState(
    localStorage.getItem("financialYearSelected") || "",
  );
  const [financialYears, setFinancialYears] = useState([]);

  // ✅ SUPPORT DIALOG STATES
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [supportCode, setSupportCode] = useState("");
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [supportConnected, setSupportConnected] = useState(false);
  const [supportSessionOpen, setSupportSessionOpen] = useState(false);
  const [connectedSupportCode, setConnectedSupportCode] = useState("");
  const [connectedDatabase, setConnectedDatabase] = useState("");

  const navigate = useNavigate();

  const handleLogout = async () => {
    const confirmLogout = await swal({
      title: "Logout?",
      text: "Are you sure you want to logout?",
      icon: "warning",
      buttons: ["Cancel", "Logout"],
      dangerMode: true,
    });

    if (!confirmLogout) return;

    localStorage.removeItem("Companydetail");
    localStorage.removeItem("allowedPermission");
    localStorage.removeItem("token");
    localStorage.removeItem("userData");

    setMobileOpen(false);
    navigate("/login", { replace: true });
  };

  const openProfilePage = () => {
    setMobileOpen(false);
    navigate("/app/profile");
  };

  const openSupportDialog = () => {
    setMobileOpen(false);
    setSupportDialogOpen(true);
    setSupportMessage("");
    setSupportConnected(false);
  };

  const closeSupportDialog = () => {
    setSupportDialogOpen(false);
    setSupportCode("");
    setSupportMessage("");
    setSupportConnected(false);
    setSupportLoading(false);
  };

  const verifySupportCode = async () => {
    try {
      const cleanCode = String(supportCode || "").trim();

      if (!cleanCode) {
        setSupportConnected(false);
        setSupportMessage("Please enter support code");
        return;
      }

      if (cleanCode.length !== 6) {
        setSupportConnected(false);
        setSupportMessage("Please enter valid 6 digit code");
        return;
      }

      setSupportLoading(true);
      setSupportMessage("");

      const res = await fetch(VERIFY_SUPPORT_CODE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code: cleanCode,
        }),
      });

      const data = await res.json();

      if (data?.connected || data?.status) {
        setSupportConnected(true);
        setSupportMessage("Connected");
        setConnectedSupportCode(cleanCode);
        setConnectedDatabase(data?.database || "");
        setSupportDialogOpen(false);
        setSupportSessionOpen(true);
      } else {
        setSupportConnected(false);
        setSupportMessage(data?.message || "Not connected");
      }
    } catch (error) {
      console.log("Support code verify error:", error);
      setSupportConnected(false);
      setSupportMessage("Not connected");
    } finally {
      setSupportLoading(false);
    }
  };

  const normalizeFinancialYears = (value) => {
    let list = value;

    if (typeof list === "string") {
      try {
        list = JSON.parse(list);
      } catch {
        list = list
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
      }
    }

    if (!Array.isArray(list)) return [];

    return [
      ...new Set(list.map((x) => String(x || "").trim()).filter(Boolean)),
    ];
  };

  const getFyStartYear = (fy) => {
    const year = Number(String(fy || "").split("-")[0]);
    return Number.isFinite(year) ? year : 0;
  };

  const sortFinancialYears = (list = []) => {
    return [...list].sort((a, b) => getFyStartYear(a) - getFyStartYear(b));
  };

  const loadFinancialYears = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");

      if (!userData?._id || !userData?.database) {
        const userYears = normalizeFinancialYears(userData?.financialYears);
        setFinancialYears(sortFinancialYears(userYears));
        return;
      }

      const res = await ViewCompanyDetails(userData?._id, userData?.database);

      const companyDetails =
        res?.CompanyDetail ||
        res?.companyDetail ||
        res?.data?.CompanyDetail ||
        res?.data?.companyDetail ||
        res?.data ||
        {};

      const yearsFromCompany = normalizeFinancialYears(
        companyDetails?.financialYears,
      );

      const yearsFromUser = normalizeFinancialYears(userData?.financialYears);

      const finalYears = yearsFromCompany.length
        ? yearsFromCompany
        : yearsFromUser;

      const sortedYears = sortFinancialYears(finalYears);

      setFinancialYears(sortedYears);

      const savedFY = localStorage.getItem("financialYearSelected") || "";

      if (savedFY && sortedYears.length && !sortedYears.includes(savedFY)) {
        localStorage.removeItem("financialYearSelected");
        setSelectedFY("");
      }
    } catch (error) {
      console.log("Financial year load error:", error);

      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const fallbackYears = normalizeFinancialYears(userData?.financialYears);

      setFinancialYears(sortFinancialYears(fallbackYears));
    }
  };

  const handleFinancialYearChange = async (e) => {
    const newFY = e.target.value;

    if (!newFY) return;

    const oldFY = selectedFY || localStorage.getItem("financialYearSelected");

    if (oldFY === newFY) return;

    const confirm = await swal({
      title: "Switch Financial Year?",
      text: `Do you want to switch financial year${
        oldFY ? ` from ${oldFY}` : ""
      } to ${newFY}?`,
      icon: "warning",
      buttons: ["No", "Yes"],
      dangerMode: false,
    });

    if (confirm) {
      localStorage.setItem("financialYearSelected", newFY);
      setSelectedFY(newFY);

      window.dispatchEvent(
        new CustomEvent("financialYearSelected:updated", {
          detail: { financialYear: newFY },
        }),
      );

      swal(
        "Financial year switched",
        `Selected Financial Year: ${newFY}`,
        "success",
      );
    }
  };

  // ✅ NEW: main menu direct click when it has only one sub-item
  const getValidItems = (menu) => {
    return Array.isArray(menu?.items) ? menu.items.filter(Boolean) : [];
  };

  const isSinglePageMenu = (menu) => {
    const items = getValidItems(menu);
    return items.length === 1 && !!items[0]?.to;
  };

  const openSinglePageMenu = (menu) => {
    const item = getValidItems(menu)[0];
    if (!item?.to) return;

    setMobileOpen(false);
    navigate(item.to);
  };

  const renderMainMenu = (m, mobile = false) => {
    if (isSinglePageMenu(m)) {
      return (
        <button
          key={mobile ? `m-single-${m.id}` : `single-${m.id}`}
          type="button"
          className={
            mobile
              ? "single-menu-btn mobile-single-menu-btn"
              : "single-menu-btn"
          }
          onClick={() => openSinglePageMenu(m)}
          title={getValidItems(m)[0]?.label || m.label}
        >
          {typeof Icon === "function" && m?.icon ? (
            <span className="single-menu-icon">
              <Icon name={m.icon} />
            </span>
          ) : null}
          <span>{m.label}</span>
        </button>
      );
    }

    return (
      <Dropdown
        key={mobile ? `m-${m.id}` : m.id}
        label={m.label}
        icon={m.icon}
        items={m.items}
      />
    );
  };

  useEffect(() => {
    if (document.getElementById("topnav-inline-css")) return;

    const s = document.createElement("style");
    s.id = "topnav-inline-css";
    s.innerHTML = `
      .topnav{
        position:sticky;
        top:0;
        z-index:50;
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:12px;
        padding:8px 12px;
        background:var(--topnav-bg, #fff);
        border-bottom:1px solid #e5e7eb;
        box-shadow:0 8px 22px rgba(15,23,42,0.06);
      }

      .dark .topnav{
        background:#0b1220;
        border-color:#1f2a44;
      }

      .brand{
        display:flex;
        align-items:center;
        gap:8px;
        min-width:0;
      }

      .brand .logo-text{
        height:34px;
        width:auto;
        object-fit:contain;
      }

      .nav-center{
        display:flex;
        gap:8px;
        align-items:center;
        justify-content:center;
        flex:1;
      }

      .actions{
        display:flex;
        align-items:center;
        gap:8px;
      }

      .action-btn{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        height:36px;
        width:36px;
        border:1px solid #e5e7eb;
        border-radius:12px;
        background:#fff;
        cursor:pointer;
        transition:all .18s ease;
      }

      .action-btn:hover{
        transform:translateY(-1px);
        box-shadow:0 8px 18px rgba(15,23,42,0.12);
      }

      .dark .action-btn{
        background:#0f172a;
        border-color:#1f2a44;
        color:#fff;
      }

      .support-nav-btn{
        height:36px;
        border:1px solid #bfdbfe;
        border-radius:12px;
        padding:0 13px;
        display:inline-flex;
        align-items:center;
        justify-content:center;
        gap:7px;
        background:#eff6ff;
        color:#1d4ed8;
        font-size:13px;
        font-weight:900;
        cursor:pointer;
        transition:all .18s ease;
        white-space:nowrap;
      }

      .support-nav-btn:hover{
        transform:translateY(-1px);
        box-shadow:0 8px 18px rgba(37,99,235,0.18);
        background:#dbeafe;
      }

      .dark .support-nav-btn{
        background:#172554;
        border-color:#1d4ed8;
        color:#bfdbfe;
      }

      .single-menu-btn{
        min-height:36px;
        border:1px solid #e5e7eb;
        border-radius:12px;
        background:#fff;
        padding:0 13px;
        display:inline-flex;
        align-items:center;
        justify-content:center;
        gap:7px;
        font-size:13px;
        font-weight:800;
        color:#0f172a;
        cursor:pointer;
        text-transform:uppercase;
        transition:all .18s ease;
        white-space:nowrap;
      }

      .single-menu-btn:hover{
        transform:translateY(-1px);
        box-shadow:0 8px 18px rgba(15,23,42,0.12);
        border-color:#bfdbfe;
        color:#0b5cf6;
      }

      .single-menu-icon{
        display:inline-flex;
        align-items:center;
        justify-content:center;
      }

      .dark .single-menu-btn{
        background:#0f172a;
        border-color:#1f2a44;
        color:#fff;
      }

      .dark .single-menu-btn:hover{
        border-color:#334155;
        color:#93c5fd;
      }

      .fy-select{
        width:220px;
        padding:9px 14px;
        border-radius:8px;
        border:1px solid #d0d5dd;
        background-color:#fff;
        font-size:15px;
        font-weight:500;
        color:#344054;
        outline:none;
        cursor:pointer;
      }

      .fy-select:disabled{
        cursor:not-allowed;
        opacity:.65;
        background:#f8fafc;
      }

      .dark .fy-select{
        background:#0f172a;
        border-color:#1f2a44;
        color:#fff;
      }

      .profile-nav-btn{
        height:36px;
        border:0;
        border-radius:999px;
        padding:0 14px;
        display:inline-flex;
        align-items:center;
        justify-content:center;
        gap:8px;
        background:linear-gradient(135deg,#ff9900,#f97316);
        color:#fff;
        font-weight:800;
        font-size:13px;
        letter-spacing:.02em;
        cursor:pointer;
        box-shadow:0 8px 18px rgba(249,115,22,.22);
        transition:all .18s ease;
        white-space:nowrap;
      }

      .profile-nav-btn:hover{
        transform:translateY(-1px);
        box-shadow:0 12px 24px rgba(249,115,22,.32);
      }

      .profile-nav-avatar{
        width:24px;
        height:24px;
        border-radius:50%;
        background:rgba(255,255,255,.22);
        display:grid;
        place-items:center;
        font-size:11px;
        font-weight:900;
      }

      .hamburger{
        display:none;
        height:36px;
        width:36px;
        border-radius:12px;
        border:1px solid #e5e7eb;
        background:#fff;
        align-items:center;
        justify-content:center;
        cursor:pointer;
      }

      .dark .hamburger{
        background:#0f172a;
        border-color:#1f2a44;
        color:#fff;
      }

      .mobile-panel{
        position:fixed;
        left:0;
        right:0;
        top:56px;
        z-index:49;
        background:var(--panel-bg, #fff);
        border-bottom:1px solid #e5e7eb;
        padding:12px;
        display:grid;
        row-gap:10px;
        animation:topnav-slide .18s ease-out;
        box-shadow:0 16px 30px rgba(15,23,42,.12);
      }

      .dark .mobile-panel{
        background:#0b1220;
        border-color:#1f2a44;
      }

      @keyframes topnav-slide{
        from{
          opacity:0;
          transform:translateY(-6px);
        }
        to{
          opacity:1;
          transform:translateY(0);
        }
      }

      .mobile-row{
        display:flex;
        flex-wrap:wrap;
        gap:8px;
      }

      .mobile-actions{
        display:flex;
        flex-wrap:wrap;
        gap:8px;
        align-items:center;
      }

      .mobile-single-menu-btn{
        height:38px;
        flex:0 0 auto;
      }

      .mobile-profile-btn{
        width:100%;
        height:42px;
        border:0;
        border-radius:14px;
        background:linear-gradient(135deg,#111827,#334155);
        color:#fff;
        font-weight:900;
        letter-spacing:.03em;
        display:flex;
        align-items:center;
        justify-content:center;
        gap:8px;
        cursor:pointer;
      }

      .mobile-support-btn{
        width:100%;
        height:42px;
        border:0;
        border-radius:14px;
        background:linear-gradient(135deg,#2563eb,#1d4ed8);
        color:#fff;
        font-weight:900;
        letter-spacing:.03em;
        display:flex;
        align-items:center;
        justify-content:center;
        gap:8px;
        cursor:pointer;
      }

      .mobile-fy-select{
        width:100%;
        height:42px;
        padding:8px 12px;
        border-radius:12px;
        border:1px solid #d0d5dd;
        background:#fff;
        font-size:14px;
        font-weight:700;
        color:#344054;
        outline:none;
      }

      .dark .mobile-fy-select{
        background:#0f172a;
        border-color:#1f2a44;
        color:#fff;
      }

      .support-dialog-overlay{
        position:fixed;
        inset:0;
        z-index:9999;
        background:rgba(15,23,42,.55);
        display:flex;
        align-items:center;
        justify-content:center;
        padding:18px;
      }

      .support-dialog{
        width:420px;
        max-width:100%;
        border-radius:18px;
        background:#fff;
        padding:22px;
        box-shadow:0 24px 60px rgba(15,23,42,.28);
        border:1px solid #e5e7eb;
      }

      .dark .support-dialog{
        background:#0b1220;
        border-color:#1f2a44;
        color:#fff;
      }

      .support-dialog-title{
        margin:0;
        font-size:22px;
        font-weight:900;
        color:#111827;
      }

      .dark .support-dialog-title{
        color:#fff;
      }

      .support-dialog-desc{
        margin:8px 0 0;
        color:#64748b;
        font-size:14px;
        line-height:21px;
      }

      .dark .support-dialog-desc{
        color:#cbd5e1;
      }

      .support-code-input{
        width:100%;
        margin-top:18px;
        height:52px;
        border-radius:12px;
        border:1px solid #d1d5db;
        padding:0 14px;
        text-align:center;
        font-size:24px;
        font-weight:900;
        letter-spacing:7px;
        outline:none;
        box-sizing:border-box;
        color:#111827;
        background:#fff;
      }

      .support-code-input:focus{
        border-color:#2563eb;
        box-shadow:0 0 0 4px rgba(37,99,235,.12);
      }

      .dark .support-code-input{
        background:#0f172a;
        border-color:#334155;
        color:#fff;
      }

      .support-status{
        margin-top:14px;
        padding:12px;
        border-radius:12px;
        font-size:15px;
        font-weight:900;
        text-align:center;
      }

      .support-status.connected{
        background:#dcfce7;
        color:#166534;
      }

      .support-status.not-connected{
        background:#fee2e2;
        color:#991b1b;
      }

      .support-dialog-actions{
        margin-top:20px;
        display:flex;
        justify-content:flex-end;
        gap:10px;
      }

      .support-cancel-btn{
        min-width:92px;
        height:40px;
        border-radius:10px;
        border:1px solid #d1d5db;
        background:#fff;
        color:#111827;
        font-weight:800;
        cursor:pointer;
      }

      .support-connect-btn{
        min-width:104px;
        height:40px;
        border-radius:10px;
        border:0;
        background:#2563eb;
        color:#fff;
        font-weight:900;
        cursor:pointer;
      }

      .support-connect-btn:disabled{
        opacity:.65;
        cursor:not-allowed;
      }

      .dark .support-cancel-btn{
        background:#0f172a;
        color:#fff;
        border-color:#334155;
      }

      @media (max-width: 768px){
        .nav-center{
          display:none;
        }

        .hamburger{
          display:inline-flex;
        }

        .brand .logo-text{
          height:28px;
        }

        .profile-nav-btn{
          display:none;
        }

        .support-nav-btn{
          display:none;
        }

        .actions > .fy-select{
          display:none;
        }
      }
    `;

    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    loadFinancialYears();

    const onUserDataUpdated = () => {
      loadFinancialYears();
    };

    window.addEventListener("userData:updated", onUserDataUpdated);

    return () => {
      window.removeEventListener("userData:updated", onUserDataUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 768 && mobileOpen) {
        setMobileOpen(false);
      }
    };

    const onKey = (e) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        if (supportDialogOpen) closeSupportDialog();
      }
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen, supportDialogOpen]);

  return (
    <>
      <header className="topnav" role="navigation" aria-label="Primary">
        <div className="brand">
          <img className="logo-text" src="/logomain.png" alt="Logo" />
        </div>

        <nav className="nav-center" aria-label="Main menus">
          {menus.map((m) => renderMainMenu(m, false))}
        </nav>

        <div className="actions" aria-label="Quick actions">
          <button
            className="hamburger"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {typeof Icon === "function" ? (
              <Icon name={mobileOpen ? "x" : "menu"} />
            ) : (
              <span>{mobileOpen ? "✕" : "☰"}</span>
            )}
          </button>

          <select
            className="fy-select"
            value={selectedFY}
            onChange={handleFinancialYearChange}
            disabled={!financialYears.length}
          >
            <option value="">
              {financialYears.length
                ? "Select Financial Year"
                : "No Financial Year"}
            </option>

            {financialYears.map((fy) => (
              <option key={fy} value={fy}>
                {fy}
              </option>
            ))}
          </select>

          {/* ✅ NEW SUPPORT BUTTON */}
          <button
            className="support-nav-btn"
            type="button"
            title="Connect App Support"
            onClick={openSupportDialog}
          >
            Support
          </button>

          <button
            className="profile-nav-btn"
            type="button"
            title="Open Profile"
            onClick={openProfilePage}
          >
            <span className="profile-nav-avatar">AJ</span>
            Profile
          </button>

          <button
            className="action-btn"
            type="button"
            title="Search (Ctrl/⌘+K)"
            onClick={onOpenPalette}
          >
            <Icon name="list" />
          </button>

          <button
            className="action-btn"
            type="button"
            title="Theme"
            onClick={toggle}
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} />
          </button>

          <button
            className="action-btn"
            type="button"
            title="Logout"
            onClick={handleLogout}
          >
            <span style={{ fontSize: "18px" }}>⇥</span>
          </button>

          {/* <ProfileMenu /> */}
        </div>
      </header>

      {mobileOpen && (
        <div
          id="mobile-menu"
          className="mobile-panel"
          role="dialog"
          aria-modal="false"
        >
          <button
            type="button"
            className="mobile-profile-btn"
            onClick={openProfilePage}
          >
            Open Full Profile Page
          </button>

          {/* ✅ NEW MOBILE SUPPORT BUTTON */}
          <button
            type="button"
            className="mobile-support-btn"
            onClick={openSupportDialog}
          >
            Connect App Support
          </button>

          <select
            className="mobile-fy-select"
            value={selectedFY}
            onChange={async (e) => {
              await handleFinancialYearChange(e);
              setMobileOpen(false);
            }}
            disabled={!financialYears.length}
          >
            <option value="">
              {financialYears.length
                ? "Select Financial Year"
                : "No Financial Year"}
            </option>

            {financialYears.map((fy) => (
              <option key={`mobile-${fy}`} value={fy}>
                {fy}
              </option>
            ))}
          </select>

          <div className="mobile-row" aria-label="Mobile menus">
            {menus.map((m) => renderMainMenu(m, true))}
          </div>

          <div className="mobile-actions" style={{ marginTop: 6 }}>
            <button
              className="action-btn"
              type="button"
              title="Search (Ctrl/⌘+K)"
              onClick={() => {
                onOpenPalette?.();
                setMobileOpen(false);
              }}
            >
              <Icon name="list" />
            </button>

            <button
              className="action-btn"
              type="button"
              title="Theme"
              onClick={() => {
                toggle();
              }}
            >
              <Icon name={theme === "dark" ? "sun" : "moon"} />
            </button>

            <button className="action-btn" type="button" title="Notifications">
              <Icon name="bell" />
            </button>
          </div>
        </div>
      )}

      {/* ✅ SUPPORT CODE DIALOG */}
      {supportDialogOpen && (
        <div
          className="support-dialog-overlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeSupportDialog();
          }}
        >
          <div className="support-dialog">
            <h2 className="support-dialog-title">Connect App Support</h2>

            <p className="support-dialog-desc">
              Enter the 6 digit code shown in the mobile app support screen.
            </p>

            <input
              className="support-code-input"
              value={supportCode}
              onChange={(e) => {
                const onlyNumbers = e.target.value.replace(/\D/g, "");
                setSupportCode(onlyNumbers.slice(0, 6));
                setSupportMessage("");
                setSupportConnected(false);
              }}
              placeholder="000000"
              maxLength={6}
              inputMode="numeric"
              autoFocus
            />

            {supportMessage ? (
              <div
                className={`support-status ${
                  supportConnected ? "connected" : "not-connected"
                }`}
              >
                {supportMessage}
              </div>
            ) : null}

            <div className="support-dialog-actions">
              <button
                type="button"
                className="support-cancel-btn"
                onClick={closeSupportDialog}
              >
                Cancel
              </button>

              <button
                type="button"
                className="support-connect-btn"
                onClick={verifySupportCode}
                disabled={supportLoading}
              >
                {supportLoading ? "Checking..." : "Connect"}
              </button>
            </div>
          </div>
        </div>
      )}

      <SupportSessionDialog
        open={supportSessionOpen}
        code={connectedSupportCode}
        database={connectedDatabase}
        onClose={() => {
          setSupportSessionOpen(false);
          setConnectedSupportCode("");
          setConnectedDatabase("");
        }}
      />
    </>
  );
}
